import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Save, Loader2 } from 'lucide-react';
import { updateDoctor, type DoctorDTO } from '@/services/doctors';
import { toast } from 'sonner';

interface DoctorBiographySettingsProps {
  doctor: DoctorDTO;
  onUpdate: (updatedDoctor: DoctorDTO) => void;
}

export function DoctorBiographySettings({ doctor, onUpdate }: DoctorBiographySettingsProps) {
  const [biography, setBiography] = useState(doctor.biography || '');
  const [isSaving, setIsSaving] = useState(false);

  const MAX_LENGTH = 1000;

  const handleSave = async () => {
    if (biography.length > MAX_LENGTH) {
      toast.error(`A biografia não pode exceder ${MAX_LENGTH} caracteres.`);
      return;
    }

    try {
      setIsSaving(true);
      const response = await updateDoctor(doctor.id, {
        biography: biography.trim(),
      });

      if (response.success && response.data) {
        toast.success('Biografia atualizada com sucesso!');
        onUpdate(response.data);
      } else {
        toast.error(typeof response.error === 'string' ? response.error : 'Erro ao atualizar biografia.');
      }
    } catch (error) {
      console.error('Error updating biography:', error);
      toast.error('Erro ao salvar biografia.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="border-gray-200 dark:border-neutral-800 shadow-xs">
      <CardHeader className="border-b border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-800/30">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-teal-600" />
          <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
            Biografia Profissional
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-bold text-gray-700 dark:text-gray-300">
              Sobre você
            </Label>
            <span className={`text-xs ${biography.length > MAX_LENGTH ? 'text-red-500 font-bold' : 'text-gray-500'}`}>
              {biography.length} / {MAX_LENGTH}
            </span>
          </div>
          <Textarea
            placeholder="Conte um pouco sobre sua experiência, especializações e abordagem médica..."
            value={biography}
            onChange={(e) => setBiography(e.target.value)}
            className="min-h-[120px] bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 resize-y"
          />
          <p className="text-[11px] text-gray-500 italic mt-1">
            Esta biografia será exibida no seu perfil público para os pacientes.
          </p>
        </div>

        <div className="pt-2">
          <Button
            onClick={handleSave}
            disabled={isSaving || biography.length > MAX_LENGTH}
            className="w-full md:w-auto bg-teal-600 hover:bg-teal-700 text-white font-bold px-8"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Biografia
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
